/**
 * A/B Testing Utility
 *
 * Simple client-side A/B test assignment via localStorage.
 * Each experiment gets a persistent variant assigned on first visit.
 * Variants are 50/50 split by default.
 */

const AB_TEST_STORAGE_KEY = 'studierai_ab_tests';

/**
 * Get all stored A/B test assignments
 */
const getStoredTests = () => {
  try {
    const stored = localStorage.getItem(AB_TEST_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Save A/B test assignments
 */
const saveTests = (tests) => {
  try {
    localStorage.setItem(AB_TEST_STORAGE_KEY, JSON.stringify(tests));
  } catch {
    // Silently fail if localStorage is unavailable
  }
};

/**
 * Get the variant for a given experiment.
 * Assigns a variant on first call and persists it.
 *
 * @param {string} experimentId - Unique experiment identifier
 * @param {string[]} variants - Array of variant names (default: ['A', 'B'])
 * @returns {string} The assigned variant
 */
export const getVariant = (experimentId, variants = ['A', 'B']) => {
  const tests = getStoredTests();

  if (tests[experimentId]) {
    // Check that the stored variant is still valid
    if (variants.includes(tests[experimentId])) {
      return tests[experimentId];
    }
  }

  // Assign new variant (equal probability)
  const randomIndex = Math.floor(Math.random() * variants.length);
  const assigned = variants[randomIndex];

  tests[experimentId] = assigned;
  saveTests(tests);

  return assigned;
};

const AB_SEED_STORAGE_KEY = 'studierai_ab_seed';

/**
 * Identita' stabile su cui appoggiare un'assegnazione deterministica.
 *
 * Primo criterio: l'uid Firebase, che il repo tiene gia' in `localStorage.userId`.
 * Con quello la stessa persona cade nello stesso braccio anche da un altro
 * dispositivo. In onboarding pero' l'uid arriva solo alla fine, quindi nella
 * pratica quasi sempre si usa il ripiego: un id anonimo scritto una volta e
 * riletto per sempre, stabile fra le sessioni di quel browser.
 */
export const getStableSeed = () => {
  try {
    const rawUserId = localStorage.getItem('userId');
    if (rawUserId) {
      const uid = JSON.parse(rawUserId);
      if (uid && typeof uid === 'string') return `uid:${uid}`;
    }
  } catch {
    // userId assente o non JSON: si passa all'id anonimo
  }

  try {
    const existing = localStorage.getItem(AB_SEED_STORAGE_KEY);
    if (existing) return existing;
    const generated = `anon:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(AB_SEED_STORAGE_KEY, generated);
    return generated;
  } catch {
    // localStorage non disponibile: nessun seme stabile
    return null;
  }
};

/**
 * Hash FNV-1a a 32 bit, ridotto a un secchio 0-99.
 * Serve solo a spargere in modo ripetibile: stessa stringa, stesso secchio,
 * su ogni dispositivo e a ogni caricamento.
 */
export const hashToBucket = (input) => {
  let hash = 0x811c9dc5;
  const text = String(input ?? '');
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash % 100;
};

/**
 * Variante deterministica con quota regolabile.
 *
 * Differenze da `getVariant`, che resta com'era per il test genitori:
 *  - la quota non e' 50/50 ma un numero (`share` = percentuale che finisce su
 *    `variants[1]`);
 *  - la prima assegnazione non e' `Math.random()` a ogni caricamento ma
 *    l'hash di un'identita' stabile, quindi chi ricarica non cambia braccio
 *    nemmeno se lo storage viene riletto in un altro ordine;
 *  - una volta assegnata, la variante e' scritta nello stesso archivio di
 *    `getVariant` e da li' in poi vince lei: se qualcuno sposta la quota, chi
 *    e' gia' dentro non si muove.
 *
 * @param {string} experimentId
 * @param {{variants?: string[], share?: number, identity?: string|null}} options
 * @returns {string}
 */
export const getVariantByShare = (experimentId, options = {}) => {
  const { variants = ['A', 'B'], share = 50, identity = null } = options;
  const tests = getStoredTests();

  if (tests[experimentId] && variants.includes(tests[experimentId])) {
    return tests[experimentId];
  }

  const seed = identity || getStableSeed();
  // Senza seme stabile (localStorage spento) non si puo' promettere stabilita':
  // meglio la variante di controllo che un braccio diverso a ogni pagina.
  const assigned = seed === null
    ? variants[0]
    : (hashToBucket(`${experimentId}:${seed}`) < share ? variants[1] : variants[0]);

  tests[experimentId] = assigned;
  saveTests(tests);

  return assigned;
};

/**
 * Force a specific variant for testing/debugging.
 * Usage: in browser console, call window.__forceABVariant('experiment_id', 'B')
 */
export const forceVariant = (experimentId, variant) => {
  const tests = getStoredTests();
  tests[experimentId] = variant;
  saveTests(tests);
  return variant;
};

// Expose for debugging
if (typeof window !== 'undefined') {
  window.__forceABVariant = forceVariant;
  window.__getABTests = getStoredTests;
}

// Experiment IDs
export const EXPERIMENTS = {
  PARENT_OFFER_EMAIL: 'parent_offer_email_v1',
  // Percorso d'ingresso corto (5 schermate) contro lungo (quello di prima).
  ONBOARDING_FLOW_LENGTH: 'onboarding_flow_length_v1',
};
